import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';

/** Les tutos vidéo de l'éditeur (Hector, 23-26.10.2026), servis par le site : /assets/video/tutos/.
 * Paddy les propose dans son panneau (bas droite) et au bon moment (textes, bibliothèque, couleurs…).
 * Mêmes fichiers et même version que la page « Créer ta planche » (assets/tuto-video.js). */
const V = '20261026b';
const url = (f: string, ext: string) => `/assets/video/tutos/${f}.${ext}?v=${V}`;

export interface Tuto {
	n: number; // 0 = le tuto complet
	f: string;
	d: number; // secondes
	t: string; // titre (les vidéos sont en français)
}

export const TUTOS: Tuto[] = [
	{ n: 0, f: 'tuto-editeur', d: 104, t: 'Tout l’éditeur' },
	{ n: 1, f: 'tuto-ed1', d: 45, t: "L'éditeur en un coup d'œil" },
	{ n: 2, f: 'tuto-ed2', d: 56, t: 'Importer un logo' },
	{ n: 3, f: 'tuto-ed3', d: 65, t: 'Enlever un fond' },
	{ n: 4, f: 'tuto-ed4', d: 53, t: 'Alléger ton transfert' },
	{ n: 5, f: 'tuto-ed5', d: 57, t: 'Finir ton logo' },
	{ n: 6, f: 'tuto-ed6', d: 57, t: 'Ta bibliothèque' },
	{ n: 7, f: 'tuto-ed7', d: 52, t: 'Ta planche' },
	{ n: 8, f: 'tuto-ed8', d: 68, t: 'Numéros, prénoms et textes' },
	{ n: 9, f: 'tuto-ed9', d: 36, t: 'Le prix et la commande' },
	{ n: 10, f: 'tuto-ed10', d: 61, t: 'Ton logo en une seule couleur' },
];

export const duree = (d: number) => (d >= 60 ? `${Math.floor(d / 60)} min ${String(d % 60).padStart(2, '0')}` : `${d} s`);

const EVT = 'dtf:tuto';
/** Ouvre la fenêtre vidéo sur le tuto n (0 = complet). Utilisable de n'importe quel panneau. */
export const openTuto = (n: number) => window.dispatchEvent(new CustomEvent(EVT, { detail: n }));

/** Petit lien « ▶ Voir le tuto · 1 min 08 » à poser dans une bulle de Paddy. */
export const TutoLink: React.FC<{ n: number }> = ({ n }) => {
	const tu = TUTOS.find(x => x.n === n);
	if (!tu) return null;
	return (
		<button type="button" className="dtf-tuto-link" data-testid={`tuto-link-${n}`} onClick={() => openTuto(n)}>
			<span className="dtf-tuto-play" aria-hidden="true">▶</span>
			{i18next.t('dtf.tuto-voir', { titre: tu.t, duree: duree(tu.d) })}
		</button>
	);
};

/** Liste des 10 tutos (panneau de Paddy). */
export const TutoList: React.FC = () => (
	<div className="dtf-tuto-list" data-testid="tuto-list">
		<h4>{i18next.t('dtf.tutos-titre')}</h4>
		{TUTOS.map(tu => (
			<button key={tu.f} type="button" className="dtf-tuto-item" data-testid={`tuto-item-${tu.n}`} onClick={() => openTuto(tu.n)}>
				<b>{tu.n === 0 ? '▶' : tu.n}</b>
				<span>{tu.t}</span>
				<i>{duree(tu.d)}</i>
			</button>
		))}
	</div>
);

/** Fenêtre vidéo, montée une fois dans l'éditeur ; s'ouvre sur openTuto(n). */
export const TutoHost: React.FC = () => {
	const [cur, setCur] = useState<number | null>(null);
	const vid = useRef<HTMLVideoElement>(null);
	const closeRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		const on = (e: Event) => setCur(Number((e as CustomEvent).detail) || 0);
		window.addEventListener(EVT, on);
		return () => window.removeEventListener(EVT, on);
	}, []);
	useEffect(() => {
		if (cur === null) return undefined;
		closeRef.current?.focus();
		const p = vid.current?.play();
		if (p && p.catch) p.catch((): void => undefined);
		const key = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				setCur(null);
			}
		};
		window.addEventListener('keydown', key, true);
		return () => window.removeEventListener('keydown', key, true);
	}, [cur]);
	if (cur === null) return null;
	const tu = TUTOS.find(x => x.n === cur) || TUTOS[0];
	return (
		<div
			className="dtf-tuto-ov"
			role="dialog"
			aria-modal="true"
			aria-label={i18next.t('dtf.tutos-titre') as string}
			data-testid="tuto-overlay"
			onClick={e => {
				if (e.target === e.currentTarget) setCur(null);
			}}
		>
			<div className="dtf-tuto-box">
				<video key={tu.f} ref={vid} controls playsInline preload="auto" poster={url(tu.f, 'jpg')} src={url(tu.f, 'mp4')} data-testid="tuto-video" />
				<button ref={closeRef} type="button" className="dtf-tuto-x" aria-label={i18next.t('dtf.fermer') as string} onClick={() => setCur(null)}>
					×
				</button>
				<div className="dtf-tuto-ch">
					{TUTOS.map(x => (
						<button key={x.f} type="button" aria-current={x.n === cur} onClick={() => setCur(x.n)}>
							<b>{x.n === 0 ? '▶' : x.n}</b>
							<span>{x.t}</span>
							<i>{duree(x.d)}</i>
						</button>
					))}
				</div>
			</div>
		</div>
	);
};
